import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import API from '@/apiRequest';

export const useClients = () => {
  const queryClient = useQueryClient();

  const useInfiniteClientsList = (filters = {}, options = {}) => useInfiniteQuery({
    queryKey: ['clients', filters],
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await API.get('/clients', {
        params: {
          search: filters.text || undefined,
          offset: pageParam,
          limit: 15,
          order_by: filters.order_by || 'name',
          order_by_dir: filters.order_by_dir || 'asc',
          status: filters.status || 'enabled',
          billing_modality: filters.billing_modality || undefined,
        },
      });
      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < 15) return undefined;
      return allPages.length * 15;
    },
    initialPageParam: 0,
    ...options,
  });


  const useClientDetails = (clientId, options = {}) => useQuery({
    queryKey: ['clients', clientId, 'details'],
    queryFn: async () => {
      const { data } = await API.get(`/view/clients/${clientId}`);
      return data;
    },
    enabled: !!clientId,
    ...options,
  });

  const useClientFullDetails = (clientId, options = {}) => useQuery({
    queryKey: ['clients', clientId, 'full'],
    queryFn: async () => {
      const { data } = await API.get(`/clients/${clientId}`);
      return data;
    },
    enabled: !!clientId,
    ...options,
  });

  const useUpdateClientStatusMutation = (clientId) => useMutation({
    mutationFn: async ({ isDisabled }) => {
      const { data } = await API.put(`/clients/${clientId}`, { is_disabled: isDisabled });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const useCreateClientMutation = () => useMutation({
    mutationFn: async (clientData) => {
      const { data } = await API.post('/clients', clientData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const useClientAccounts = (clientId, summaryType, filters = {}, options = {}) => useQuery({
    queryKey: ['clients', clientId, 'accounts', summaryType, filters],
    queryFn: async () => {
      const { data } = await API.get(`/clients/${clientId}/accounts/${summaryType}`, {
        params: {
          offset: filters.offset || 0,
          limit: filters.limit || 15,
          order_by: 'created_at',
          order_by_dir: 'desc',
          start_date: filters.startDate || undefined,
          end_date: filters.endDate || undefined,
        },
      });
      return data;
    },
    enabled: !!clientId && !!summaryType,
    ...options,
  });

  const useExportClientSummaryMutation = (clientId) => useMutation({
    mutationFn: async ({ summaryType, startDate, endDate }) => {
      const type = summaryType === 'summary' ? 'account' : summaryType;
      const { data } = await API.get(`/clients/${clientId}/accounts/export/${type}`, {
        params: {
          start_date: startDate || null,
          end_date: endDate || null,
        },
      });
      return data;
    },
  });

  return {
    useInfiniteClientsList,
    useClientDetails,
    useClientFullDetails,
    useUpdateClientStatusMutation,
    useClientAccounts,
    useExportClientSummaryMutation,
    useCreateClientMutation,
  };
};

